import type { Activity } from './types';
import seed from '../../data/activities.json';

const API = '/api/activities';
const LS_KEY = 'borgakungen.activities.v1';

export class AuthError extends Error {
  constructor(message = 'Sessionen gick ut — logga in igen.') {
    super(message);
    this.name = 'AuthError';
  }
}

function readLocal(): Activity[] | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as Activity[]) : null;
  } catch {
    return null;
  }
}

function writeLocal(items: Activity[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota errors */
  }
}

async function tryFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(input, init);
    if (res.status === 401 || res.status === 403) throw new AuthError();
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (e) {
    if (e instanceof AuthError) throw e;
    return null;
  }
}

export async function listActivities(): Promise<Activity[]> {
  const fromApi = await tryFetch<Activity[]>(API);
  if (fromApi) {
    writeLocal(fromApi);
    return fromApi;
  }
  return readLocal() ?? (seed as Activity[]);
}

export async function createActivity(item: Activity): Promise<Activity[]> {
  const fromApi = await tryFetch<Activity[]>(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(item),
  });
  if (fromApi) {
    writeLocal(fromApi);
    return fromApi;
  }
  const next = [...(readLocal() ?? (seed as Activity[])), item];
  writeLocal(next);
  return next;
}

export async function updateActivity(id: string, item: Activity): Promise<Activity[]> {
  const fromApi = await tryFetch<Activity[]>(`${API}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(item),
  });
  if (fromApi) {
    writeLocal(fromApi);
    return fromApi;
  }
  const next = (readLocal() ?? (seed as Activity[])).map((s) => (s.id === id ? item : s));
  writeLocal(next);
  return next;
}

export async function deleteActivity(id: string): Promise<Activity[]> {
  const fromApi = await tryFetch<Activity[]>(`${API}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (fromApi) {
    writeLocal(fromApi);
    return fromApi;
  }
  const next = (readLocal() ?? (seed as Activity[])).filter((s) => s.id !== id);
  writeLocal(next);
  return next;
}

export async function reorderActivities(ids: string[]): Promise<Activity[]> {
  const fromApi = await tryFetch<Activity[]>(`${API}/reorder`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (fromApi) {
    writeLocal(fromApi);
    return fromApi;
  }
  const local = readLocal() ?? (seed as Activity[]);
  const idMap = new Map(local.map((a) => [a.id, a]));
  const reordered = ids.map((i) => idMap.get(i)).filter((a): a is Activity => !!a);
  writeLocal(reordered);
  return reordered;
}

const CATS_API = '/api/categories';

export async function listCategories(): Promise<string[]> {
  try {
    const res = await fetch(CATS_API);
    if (res.ok) return (await res.json()) as string[];
  } catch { /* ignore */ }
  return ['Hoppborg', 'Hinderbana', 'Spel', 'Mat & Mys'];
}

export async function createCategory(name: string): Promise<string[]> {
  const res = await fetch(CATS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (res.status === 401 || res.status === 403) throw new AuthError();
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? 'Kunde inte skapa kategori');
  }
  return res.json() as Promise<string[]>;
}

export async function deleteCategory(name: string): Promise<string[]> {
  const res = await fetch(`${CATS_API}/${encodeURIComponent(name)}`, { method: 'DELETE' });
  if (res.status === 401 || res.status === 403) throw new AuthError();
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? 'Kunde inte ta bort kategori');
  }
  return res.json() as Promise<string[]>;
}
